import { useTranslation } from 'react-i18next';

import {
  ResourcePicker,
  type PickerChildren,
  type PickerQuery,
  type PickerSelection,
  type ResourcePickerProps,
} from '@nucleus-commerce/admin-kit/resource-picker';
import type { StoreProductStatus, StoreVariantPricing } from '@nucleus-commerce/api-client/admin';

import { resourcePickersApi } from '../api/resource-pickers-api';
import { resourcePickerKeys } from '../hooks/resource-picker-keys';
import { useProductFilterDefs } from '../hooks/use-product-filter-defs';
import { useProductRow, type StoreProductRow } from '../hooks/use-product-row';
import { useVariantRow } from '../hooks/use-variant-row';
import { toProductListParams } from '../lib/product-filters';

import { PICKER_PAGE_SIZE } from './product-filter-defs';

const SEARCH_BY = ['all', 'title', 'sku', 'barcode', 'id'] as const;

export interface StoreVariantPickerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  storeId: string;
  title?: string;
  confirmLabel?: string;
  /** `false` (default) for one variant, `true` for any number, a number to cap. */
  multiple?: boolean | number;
  initialQuery?: string;
  /** Only these store statuses are listed, and the Status filter is not offered. */
  statuses?: readonly StoreProductStatus[];
  /** Variant ids ticked when the picker opens. */
  initialSelectedVariantIds?: readonly string[];
  footerContent?: ResourcePickerProps<StoreProductRow, StoreVariantPricing>['footerContent'];
  /** `children` are the chosen variants with the store's effective price. */
  onConfirm: (selection: PickerSelection<StoreProductRow, StoreVariantPricing>) => unknown;
}

const productIdOf = (row: StoreProductRow) => row.productId;

/**
 * Variants of one store's products, nested under their product as in Shopify's variant picker,
 * with the store's price and availability; variants that cannot be sold are locked. The product
 * rows, filters and search are the store-scope product picker's.
 */
export function StoreVariantPicker({
  open,
  onOpenChange,
  storeId,
  title,
  confirmLabel,
  multiple = false,
  initialQuery,
  initialSelectedVariantIds,
  statuses,
  footerContent,
  onConfirm,
}: StoreVariantPickerProps) {
  const { t, i18n } = useTranslation(['resourcePickers', 'common']);
  const number = new Intl.NumberFormat(i18n.language);
  const filters = useProductFilterDefs(
    { kind: 'store', storeId },
    { omit: statuses ? ['status'] : [] },
  );
  const productRow = useProductRow();
  const variantRow = useVariantRow();
  const statusKey = statuses?.join(',');
  const list = (query: PickerQuery) => {
    const params: Record<string, string | number> = {
      limit: PICKER_PAGE_SIZE,
      ...(query.q ? { q: query.q, ...(query.searchBy ? { searchBy: query.searchBy } : {}) } : {}),
      ...toProductListParams(query.filters, 'store'),
      ...(statusKey ? { status: statusKey } : {}),
    };
    return {
      queryKey: resourcePickerKeys.storeProducts(storeId, params),
      queryFn: ({ cursor, signal }: { cursor: string | undefined; signal: AbortSignal }) =>
        resourcePickersApi.storeProducts(
          storeId,
          { ...params, ...(cursor ? { cursor } : {}) },
          signal,
        ),
    };
  };
  const childRows: PickerChildren<StoreProductRow, StoreVariantPricing> = {
    query: (row) => ({
      queryKey: resourcePickerKeys.storeVariants(storeId, row.id),
      queryFn: ({ signal }) => resourcePickersApi.storeVariants(storeId, row.id, signal),
    }),
    getId: (variant) => variant.variantId,
    getRow: (variant) => variantRow(variant),
    hasMany: (row) => row.product.variantCount > 1,
  };
  return (
    <ResourcePicker<StoreProductRow, StoreVariantPricing>
      open={open}
      onOpenChange={onOpenChange}
      title={title ?? t(multiple === false ? 'variants.titleOne' : 'variants.title')}
      confirmLabel={confirmLabel ?? t('common:resourcePicker.add')}
      searchPlaceholder={t('products.search')}
      emptyTitle={t('products.empty')}
      countLabel={(count, max) =>
        max === undefined
          ? t('variants.selected', { count, number: number.format(count) })
          : t('variants.selectedOf', {
              count,
              number: number.format(count),
              max: number.format(max),
            })
      }
      multiple={multiple}
      {...(initialQuery !== undefined ? { initialQuery } : {})}
      {...(initialSelectedVariantIds ? { initialSelectedChildIds: initialSelectedVariantIds } : {})}
      list={list}
      getId={productIdOf}
      getRow={(row) => productRow.store(row)}
      filters={filters}
      searchBy={{
        options: SEARCH_BY.map((value) => ({ value, label: t(`searchBy.${value}`) })),
        defaultValue: 'all',
      }}
      childRows={childRows}
      footerContent={footerContent}
      onConfirm={onConfirm}
    />
  );
}
